import React from "react";
import PropTypes from "prop-types";
import { Lock, Shield } from "lucide-react";
import { getCategoryStyle, exportScanResultsToCSV, handleKeyDown } from "../utils/helpers";

/**
 * ScanResults Component
 * Displays comprehensive scan results with KPI cards, distribution charts, and detailed PII analysis
 */
export const ScanResults = ({
  scanResults,
  expandedTables,
  previewTables,
  distributionFilter,
  selectedPiiColumns,
  selectedDatabase,
  onToggleTable,
  onTogglePreview,
  onSelectAllColumns,
  onDeselectAllColumns,
  onColumnCheckboxChange,
  onSetDistributionFilter,
  regionsData,
}) => {
  if (!scanResults) return null;

  // Multi-table scan results
  if (scanResults.summary) {
    return (
      <div className="space-y-6 pb-24" style={{ overflowX: "hidden", width: "100%" }}>
        {/* Summary Overview */}
        <div className="flex gap-6 items-stretch flex-wrap lg:flex-nowrap">
          {/* LEFT: KPI Card */}
          <div
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex-1"
            style={{ minWidth: "300px" }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-800">
                  PII Scan Results
                </h3>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="font-medium">Tables Scanned:</span>
                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-semibold">
                  {scanResults.total_tables_scanned}
                </span>
              </div>
            </div>

            {/* Summary Overview Cards */}
            <div
              style={{
                display: "flex",
                gap: "16px",
                flexWrap: "wrap",
                marginBottom: "24px",
              }}
            >
              {/* Total Columns */}
              <div
                role="button"
                tabIndex={0}
                className="kpi-card"
                onClick={() => onSetDistributionFilter("all")}
                onKeyDown={(e) =>
                  handleKeyDown(e, () => onSetDistributionFilter("all"))
                }
                style={{
                  flex: "1 1 calc(50% - 8px)",
                  minWidth: "150px",
                  cursor: "pointer",
                  outline:
                    distributionFilter === "all" ? "2px solid #7c3aed" : "none",
                  outlineOffset: "2px",
                }}
                title="Show all columns"
              >
                <h2 className="kpi-title">Total Columns</h2>
                <p
                  className="text-2xl font-bold mt-2"
                  style={{ color: "#003366" }}
                >
                  {scanResults.summary.total_columns}
                </p>
                <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                  Across all {scanResults.total_tables_scanned} tables
                </span>
              </div>

              {/* PII Columns */}
              <div
                role="button"
                tabIndex={0}
                className="kpi-card"
                onClick={() => onSetDistributionFilter("pii")}
                onKeyDown={(e) =>
                  handleKeyDown(e, () => onSetDistributionFilter("pii"))
                }
                style={{
                  flex: "1 1 calc(50% - 8px)",
                  minWidth: "150px",
                  cursor: "pointer",
                  outline:
                    distributionFilter === "pii" ? "2px solid #ef4444" : "none",
                  outlineOffset: "2px",
                }}
                title="Show PII columns only"
              >
                <h2 className="kpi-title">PII Columns</h2>
                <p
                  className="text-2xl font-bold mt-2"
                  style={{ color: "#ef4444" }}
                >
                  {scanResults.summary.total_pii_columns}
                </p>
                <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                  {scanResults.summary.overall_risk_percentage}% overall risk
                </span>
              </div>

              {/* Safe Columns */}
              <div
                className="kpi-card"
                onClick={() => onSetDistributionFilter("safe")}
                style={{
                  flex: "1 1 calc(50% - 8px)",
                  minWidth: "150px",
                  cursor: "pointer",
                  outline:
                    distributionFilter === "safe" ? "2px solid #22c55e" : "none",
                  outlineOffset: "2px",
                }}
                title="Show safe columns only"
              >
                <h2 className="kpi-title">Safe Columns</h2>
                <p
                  className="text-2xl font-bold mt-2"
                  style={{ color: "#22c55e" }}
                >
                  {scanResults.summary.total_safe_columns}
                </p>
                <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                  {(
                    (scanResults.summary.total_safe_columns /
                      scanResults.summary.total_columns) *
                    100
                  ).toFixed(1)}
                  % secure
                </span>
              </div>

              {/* Overall Risk */}
              <div
                className="kpi-card"
                style={{ flex: "1 1 calc(50% - 8px)", minWidth: "150px" }}
              >
                <h2 className="kpi-title">Overall Risk</h2>
                <p
                  className="text-2xl font-bold mt-2"
                  style={{
                    color:
                      scanResults.summary.overall_risk_level === "High"
                        ? "#ef4444"
                        : scanResults.summary.overall_risk_level === "Medium"
                          ? "#f59e0b"
                          : "#22c55e",
                  }}
                >
                  {scanResults.summary.overall_risk_level}
                </p>
                <span
                  style={{
                    fontSize: "12px",
                    color: "#9ca3af",
                    display: "block",
                    wordBreak: "break-all",
                    overflowWrap: "break-word",
                  }}
                >
                  Highest: {scanResults.summary.highest_risk_table}
                </span>
              </div>
            </div>
          </div>

          {/* RIGHT: Distribution Chart */}
          <div
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex-1"
            style={{ minWidth: "300px" }}
          >
            <h4 className="text-sm font-semibold text-gray-700 mb-4">
              Overall PII Distribution
            </h4>
            <div className="flex items-center justify-center gap-8 flex-wrap lg:flex-nowrap">
              <div className="flex-1 max-w-md w-full">
                {/* PII Columns Bar */}
                {(distributionFilter === "all" ||
                  distributionFilter === "pii") && (
                  <div className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-red-600 bg-red-200">
                          PII Columns
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-red-600">
                          {(
                            (scanResults.summary.total_pii_columns /
                              scanResults.summary.total_columns) *
                            100
                          ).toFixed(1)}
                          %
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-4 mb-4 text-xs flex rounded bg-red-200">
                      <div
                        style={{
                          width: `${(
                            (scanResults.summary.total_pii_columns /
                              scanResults.summary.total_columns) *
                            100
                          ).toFixed(1)}%`,
                          transition: "width 0.5s ease-in-out",
                        }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-red-500"
                      ></div>
                    </div>
                  </div>
                )}

                {/* Safe Columns Bar */}
                {(distributionFilter === "all" ||
                  distributionFilter === "safe") && (
                  <div className="relative pt-1">
                    <div className="flex mb-2 items-center justify-between">
                      <div>
                        <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-green-600 bg-green-200">
                          Safe Columns
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-semibold inline-block text-green-600">
                          {(
                            (scanResults.summary.total_safe_columns /
                              scanResults.summary.total_columns) *
                            100
                          ).toFixed(1)}
                          %
                        </span>
                      </div>
                    </div>
                    <div className="overflow-hidden h-4 mb-4 text-xs flex rounded bg-green-200">
                      <div
                        style={{
                          width: `${(
                            (scanResults.summary.total_safe_columns /
                              scanResults.summary.total_columns) *
                            100
                          ).toFixed(1)}%`,
                          transition: "width 0.5s ease-in-out",
                        }}
                        className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500"
                      ></div>
                    </div>
                  </div>
                )}
              </div>

              {/* Legend */}
              <div className="flex flex-col gap-3">
                <div
                  onClick={() => onSetDistributionFilter("pii")}
                  className={`flex items-center gap-3 px-2 py-1.5 rounded-lg cursor-pointer transition-all ${
                    distributionFilter === "pii"
                      ? "bg-red-50 ring-2 ring-red-300"
                      : "hover:bg-gray-50"
                  }`}
                  title="Click to filter PII Columns only"
                >
                  <div className="w-4 h-4 rounded bg-red-500 flex-shrink-0"></div>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">
                      {scanResults.summary.total_pii_columns}
                    </span>
                    <span className="text-gray-600"> PII Columns</span>
                  </div>
                </div>
                <div
                  onClick={() => onSetDistributionFilter("safe")}
                  className={`flex items-center gap-3 px-2 py-1.5 rounded-lg cursor-pointer transition-all ${
                    distributionFilter === "safe"
                      ? "bg-green-50 ring-2 ring-green-300"
                      : "hover:bg-gray-50"
                  }`}
                  title="Click to filter Safe Columns only"
                >
                  <div className="w-4 h-4 rounded bg-green-500 flex-shrink-0"></div>
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">
                      {scanResults.summary.total_safe_columns}
                    </span>
                    <span className="text-gray-600"> Safe Columns</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* PII Details Overview */}
        {(() => {
          const samplePiiData = scanResults.results
            .filter(
              (result) =>
                result.pii_columns && result.pii_columns.length > 0
            )
            .map((result) => ({
              table_name: result.table_name,
              schema: result.schema || "dbo",
              datasource: selectedDatabase,
              row_count: result.row_count,
              pii_columns: result.pii_columns.map((col) => ({
                column_name: col.column_name,
                pii_categories: (col.pii_types || []).map((t) =>
                  t.replaceAll("_", " ")
                ),
                compliance: col.compliance || [],
                risk: (col.risk || "LOW").toUpperCase(),
                confidence: col.confidence || "—",
                samples: col.samples || [],
              })),
            }));

          const expandAll = () =>
            Object.fromEntries(
              samplePiiData.map((t) => [t.table_name, true])
            );
          const collapseAll = () =>
            Object.fromEntries(
              samplePiiData.map((t) => [t.table_name, false])
            );
          const exportCSV = () => {
            exportScanResultsToCSV(scanResults, samplePiiData);
          };

          return (
            <div
              className="bg-white rounded-xl shadow-sm border border-gray-200"
              style={{ minWidth: 0, overflow: "hidden" }}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 className="text-base font-semibold text-gray-800">
                  PII Details Overview
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const allExpanded = samplePiiData.every(
                        (t) => expandedTables[t.table_name]
                      );
                      if (allExpanded) {
                        onToggleTable(
                          Object.fromEntries(
                            samplePiiData.map((t) => [t.table_name, false])
                          )
                        );
                      } else {
                        onToggleTable(expandAll());
                      }
                    }}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-1.5"
                  >
                    <svg
                      className={`w-3.5 h-3.5 transition-transform ${
                        samplePiiData.every(
                          (t) => expandedTables[t.table_name]
                        )
                          ? "rotate-180"
                          : "rotate-0"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                    {samplePiiData.every(
                      (t) => expandedTables[t.table_name]
                    )
                      ? "Collapse all"
                      : "Expand all"}
                  </button>
                  <button
                    onClick={exportCSV}
                    className="px-3 py-1.5 text-xs font-medium text-purple-700 border border-purple-300 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors flex items-center gap-1.5"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                    Export CSV
                  </button>
                </div>
              </div>

              {/* Tables */}
              <div className="divide-y divide-gray-100">
                {samplePiiData.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                    <Shield className="w-10 h-10 mb-3 opacity-30" />
                    <p className="text-sm font-medium">
                      No PII columns detected
                    </p>
                    <p className="text-xs mt-1">
                      All scanned tables appear to be safe
                    </p>
                  </div>
                ) : (
                  samplePiiData.map((table) => (
                    <div key={table.table_name}>
                      {/* Table Header Row */}
                      <div
                        className="flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => onToggleTable(table.table_name)}
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${
                              expandedTables[table.table_name]
                                ? "rotate-0"
                                : "-rotate-90"
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-gray-900">
                                {table.table_name}
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onTogglePreview(table.table_name);
                                }}
                                className={`p-1 rounded-md transition-colors ${
                                  previewTables[table.table_name]
                                    ? "text-purple-600 bg-purple-100"
                                    : "text-gray-400 hover:text-purple-600 hover:bg-purple-50"
                                }`}
                                title={`${
                                  previewTables[table.table_name]
                                    ? "Hide"
                                    : "Preview"
                                } sample data for ${table.table_name}`}
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                        <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                          {table.pii_columns.length} PII column
                          {table.pii_columns.length !== 1 ? "s" : ""}
                        </span>
                      </div>

                      {/* Expanded Table Content */}
                      {expandedTables[table.table_name] && (
                        <div className="px-6 pb-4">
                          <div className="rounded-lg border border-gray-200 overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10">
                                    <input
                                      type="checkbox"
                                      checked={
                                        table.pii_columns.length > 0 &&
                                        table.pii_columns.every(
                                          (col) =>
                                            selectedPiiColumns[
                                            `${table.table_name}|||${col.column_name}`
                                            ]
                                        )
                                      }
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          onSelectAllColumns(table.table_name);
                                        } else {
                                          const newSelection = {
                                            ...selectedPiiColumns,
                                          };
                                          table.pii_columns.forEach((col) => {
                                            delete newSelection[
                                              `${table.table_name}|||${col.column_name}`
                                            ];
                                          });
                                          onDeselectAllColumns(newSelection);
                                        }
                                      }}
                                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                                    />
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-40">
                                    Column
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-48">
                                    PII Categories
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-36">
                                    Compliance
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                                    Risk
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-28">
                                    Confidence
                                  </th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    Top 10 Records
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-100">
                                {table.pii_columns.map((col) => {
                                  const key = `${table.table_name}|||${col.column_name}`;
                                  const isSelected =
                                    selectedPiiColumns[key] || false;
                                  return (
                                    <tr
                                      key={key}
                                      className={`transition-colors cursor-pointer ${
                                        isSelected
                                          ? "bg-purple-50"
                                          : "hover:bg-gray-50"
                                      }`}
                                      onClick={() =>
                                        onColumnCheckboxChange(
                                          table.table_name,
                                          col.column_name
                                        )
                                      }
                                    >
                                      <td
                                        className="px-4 py-3"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isSelected}
                                          onChange={() =>
                                            onColumnCheckboxChange(
                                              table.table_name,
                                              col.column_name
                                            )
                                          }
                                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                                        />
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className="font-mono text-sm text-gray-800">
                                          {col.column_name}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                          {col.pii_categories.map((cat, i) => {
                                            const style =
                                              getCategoryStyle(cat);
                                            return (
                                              <span
                                                key={i}
                                                className={`px-2 py-0.5 text-xs font-medium rounded border ${style.bg} ${style.text} ${style.border}`}
                                              >
                                                {cat}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="flex flex-wrap gap-1">
                                          {col.compliance.map((c, i) => (
                                            <span
                                              key={i}
                                              className="px-2 py-0.5 text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded"
                                            >
                                              {c}
                                            </span>
                                          ))}
                                        </div>
                                      </td>
                                      <td className="px-4 py-3">
                                        <span
                                          className={`px-2.5 py-1 text-xs font-bold rounded ${
                                            col.risk === "HIGH"
                                              ? "bg-red-100 text-red-700"
                                              : col.risk === "MEDIUM"
                                                ? "bg-yellow-100 text-yellow-700"
                                                : "bg-green-100 text-green-700"
                                          }`}
                                        >
                                          {col.risk}
                                        </span>
                                      </td>
                                      <td className="px-4 py-3 text-sm text-gray-700 font-medium">
                                        {col.confidence}
                                      </td>
                                      <td className="px-4 py-3">
                                        <span className="text-xs text-gray-600 font-mono leading-relaxed">
                                          {col.samples.join(" | ")}
                                        </span>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })()}
      </div>
    );
  }

  // Single table scan results
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-800">
            PII Scan Results
          </h3>
        </div>
      </div>
      <p className="text-gray-600">
        Results: {scanResults.total_pii_columns || 0} PII columns detected
      </p>
    </div>
  );
};

ScanResults.propTypes = {
  scanResults: PropTypes.object,
  expandedTables: PropTypes.objectOf(PropTypes.bool).isRequired,
  previewTables: PropTypes.objectOf(PropTypes.bool).isRequired,
  distributionFilter: PropTypes.string.isRequired,
  selectedPiiColumns: PropTypes.objectOf(PropTypes.bool).isRequired,
  selectedDatabase: PropTypes.string.isRequired,
  onToggleTable: PropTypes.func.isRequired,
  onTogglePreview: PropTypes.func.isRequired,
  onSelectAllColumns: PropTypes.func.isRequired,
  onDeselectAllColumns: PropTypes.func.isRequired,
  onColumnCheckboxChange: PropTypes.func.isRequired,
  onSetDistributionFilter: PropTypes.func.isRequired,
  regionsData: PropTypes.arrayOf(PropTypes.object).isRequired,
};
